import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Award, Clock, Users } from "lucide-react";

const OMNI_BADGE = "https://customer-assets-v7afamib.emergentagent.net/job_bright-installations-1/artifacts/b35qdpuq_Omni%20Certified%20Installer%20Logo%20%281%29.png";

const stats = [
  { n: "6+", label: "Service Categories", Icon: Award },
  { n: "365", label: "Days of Programmable Lighting", Icon: Clock },
  { n: "16M", label: "Colors, One Tap", Icon: ShieldCheck },
  { n: "100%", label: "Omni Certified Team", Icon: Users },
];

export default function About() {
  return (
    <section id="about" className="section-pad relative border-t border-white/10">
      <div className="container-x">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-20 mb-14">
          <div className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
            [ About ]
          </div>
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-white text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tight max-w-4xl"
            >
              We wire architecture<br />
              <span className="text-[color:var(--primary)]">for the night.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="space-y-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl"
          >
            <p>
              Young Guns Lighting Solutions is a Wisconsin Dells lighting company that installs
              permanent architectural lighting for homes and businesses across Wisconsin and Southwest Florida. As an{" "}
              <span className="text-white">Omni RGB Certified Installer</span>, we deliver
              factory-spec results — no more seasonal setups, no more ladders in December.
            </p>
            <p>
              From full-perimeter Omni RGB systems to landscape, holiday and commercial installs,
              we design lighting that runs year-round with a single tap. Engineered once, warrantied
              hardware, one point of contact — that's the promise behind{" "}
              <span className="text-[color:var(--primary)]">Light it once. Enjoy it forever.</span>
            </p>
          </motion.div>

          {/* Prominent Omni Certified card */}
          <motion.a
            href="#services"
            onClick={(e) => { e.preventDefault(); document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" }); }}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative block border border-[color:var(--primary)]/40 p-8 md:p-10 bg-[color:var(--primary)]/[0.05] group hover:border-[color:var(--primary)] transition-colors"
          >
            <div className="absolute -top-4 -right-4 w-32 h-32 rounded-full bg-[color:var(--primary)]/25 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col items-center text-center gap-6">
              <div className="rounded-full bg-white p-3 shadow-[0_0_40px_rgba(0,229,255,0.5)] transition-transform duration-500 group-hover:scale-105">
                <img
                  src={OMNI_BADGE}
                  alt="Omni RGB Certified Installer"
                  className="h-40 w-40 md:h-48 md:w-48 object-contain"
                />
              </div>
              <div>
                <div className="font-mono-accent text-[10px] tracking-[0.35em] uppercase text-[color:var(--primary)] mb-2">
                  Factory Trained · Fully Warrantied
                </div>
                <div className="font-display uppercase text-white text-3xl md:text-4xl tracking-tight leading-none">
                  Omni RGB<br />Certified Installer
                </div>
                <p className="mt-3 text-white/60 text-sm max-w-xs mx-auto">
                  We are certified installers of the Omni Lighting system — the gold standard for permanent architectural RGB.
                </p>
              </div>
            </div>
          </motion.a>
        </div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 border border-white/10"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`p-6 md:p-8 flex flex-col items-start gap-3 ${i > 0 ? "md:border-l border-white/10" : ""} ${i > 1 ? "border-t md:border-t-0 border-white/10" : ""}`}
            >
              <s.Icon size={18} className="text-[color:var(--primary)]" />
              <div className="font-display uppercase text-white text-4xl md:text-5xl tracking-tight leading-none">
                {s.n}
              </div>
              <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/50">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
